import { App } from "@capacitor/app";
import { type ConnectionStatus, Network } from "@capacitor/network";
import { type ReactNode, createContext, useContext, useEffect, useRef, useState } from "react";
import { logger } from "@/logger";
import { WebSocketClient, type WebSocketClientOptions } from "./client";

export type ConnectionState = "connecting" | "connected" | "disconnected" | "reconnecting";

interface WebSocketContextValue {
  client: WebSocketClient | null;
  connectionState: ConnectionState;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

interface WebSocketProviderProps {
  children: ReactNode;
  options?: Omit<WebSocketClientOptions, "autoConnect">;
}

export function WebSocketProvider({ children, options }: WebSocketProviderProps) {
  const [client, setClient] = useState<WebSocketClient | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const isAppActiveRef = useRef(true);
  const backgroundTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkStatusRef = useRef<ConnectionStatus | null>(null);

  useEffect(() => {
    // Create client without auto-connect
    const wsClient = new WebSocketClient({
      ...options,
      autoConnect: false,
    });

    // Setup connection state listeners
    wsClient.on("connected", () => {
      setConnectionState("connected");
    });

    wsClient.on("disconnected", () => {
      setConnectionState("disconnected");
    });

    wsClient.on("error", () => {
      setConnectionState("reconnecting");
    });

    setClient(wsClient);

    // Connect initially
    wsClient.connect();
    setConnectionState("connecting");

    return () => {
      wsClient.disconnect();
    };
  }, [options]);

  // App lifecycle management
  useEffect(() => {
    let appStateListener: Awaited<ReturnType<typeof App.addListener>> | null = null;
    let resumeListener: Awaited<ReturnType<typeof App.addListener>> | null = null;

    const setupListeners = async () => {
      appStateListener = await App.addListener("appStateChange", ({ isActive }) => {
        isAppActiveRef.current = isActive;

        if (isActive) {
          // App became active, reconnect if disconnected
          if (backgroundTimeoutRef.current) {
            clearTimeout(backgroundTimeoutRef.current);
            backgroundTimeoutRef.current = null;
          }

          if (client && !client.isConnected) {
            logger.info("[WebSocket] App became active, reconnecting...");
            client.connect();
            setConnectionState("connecting");
          }
        } else {
          // App went to background, disconnect after grace period to save battery
          logger.info("[WebSocket] App went to background");
          backgroundTimeoutRef.current = setTimeout(() => {
            if (!isAppActiveRef.current && client?.isConnected) {
              logger.info("[WebSocket] Disconnecting for battery savings");
              client.disconnect();
              setConnectionState("disconnected");
            }
          }, 30000); // 30 second grace period
        }
      });

      resumeListener = await App.addListener("resume", () => {
        logger.info("[WebSocket] App resumed");
        if (client && !client.isConnected) {
          client.connect();
          setConnectionState("connecting");
        }
      });
    };

    setupListeners();

    return () => {
      appStateListener?.remove();
      resumeListener?.remove();
      if (backgroundTimeoutRef.current) {
        clearTimeout(backgroundTimeoutRef.current);
      }
    };
  }, [client]);

  // Network change handling
  useEffect(() => {
    let networkListener: Awaited<ReturnType<typeof Network.addListener>> | null = null;

    const setupNetworkListener = async () => {
      networkListener = await Network.addListener("networkStatusChange", (status) => {
        logger.info("[WebSocket] Network status changed:", status);
        networkStatusRef.current = status;

        if (status.connected && !client?.isConnected && isAppActiveRef.current) {
          logger.info("[WebSocket] Network restored, reconnecting...");
          client?.connect();
          setConnectionState("connecting");
        }
      });
    };

    setupNetworkListener();

    // Get initial network status
    Network.getStatus().then((status) => {
      networkStatusRef.current = status;
    });

    return () => {
      networkListener?.remove();
    };
  }, [client]);

  const value: WebSocketContextValue = {
    client,
    connectionState,
    isConnected: connectionState === "connected",
  };

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

export function useWebSocketContext(): WebSocketContextValue {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocketContext must be used within a WebSocketProvider");
  }
  return context;
}
